import React from 'react';
import { Upload, Download, Eye, FileText, FileSignature, Trash2 } from 'lucide-react';
import type { PensionFund, MultiFondationDocument } from '../../types';

export interface PensionFundDocumentsProps {
  pensionFund: PensionFund;
  getFileUrl: (path: string | null | undefined) => string;
  handleFileUpload: (field: string, file: File) => void;
  handleFileDelete: (field: string, documentName: string) => void;
  deleteDocumentMutation: { isPending: boolean };
  uploadDecompteMutation: { isPending: boolean };
  uploadDemandeTransfertMutation: { isPending: boolean };
  uploadInstructionMutation: { isPending: boolean };
  signInstructionMutation: { isPending: boolean; mutate: (fondationName: string) => void };
}

const PensionFundDocuments: React.FC<PensionFundDocumentsProps> = ({
  pensionFund,
  getFileUrl,
  handleFileUpload,
  handleFileDelete,
  deleteDocumentMutation,
  uploadDecompteMutation,
  uploadDemandeTransfertMutation,
  uploadInstructionMutation,
  signInstructionMutation,
}) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Documents de la caisse</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { label: 'Demande de transfert', field: 'demande_transfert_document', icon: FileText },
          { label: 'Instruction institution', field: 'lemania_instruction_document', icon: FileText },
          { label: 'Instruction Banque', field: 'instruction_document', icon: FileText },
          { label: 'Décompte de sortie', field: 'exit_statement_document', icon: FileText },
        ].map((doc) => {
          const Icon = doc.icon;
          const hasDocument = pensionFund[doc.field as keyof PensionFund];
          return (
            <div
              key={doc.field}
              className={`relative border-2 rounded-xl p-5 transition-all duration-200 ${
                hasDocument
                  ? 'border-green-200 bg-green-50 hover:shadow-md'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${hasDocument ? 'bg-green-100' : 'bg-gray-200'}`}>
                    <Icon className={`h-5 w-5 ${hasDocument ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">{doc.label}</h4>
                    <p className={`text-xs mt-1 ${hasDocument ? 'text-green-600' : 'text-red-600'}`}>
                      {hasDocument ? 'Disponible' : 'Non fourni'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {hasDocument ? (
                  <>
                    <a
                      href={getFileUrl(pensionFund[doc.field as keyof PensionFund] as string)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      Voir
                    </a>
                    <a
                      href={getFileUrl(pensionFund[doc.field as keyof PensionFund] as string)}
                      download
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Télécharger
                    </a>
                    <button
                      onClick={() => handleFileDelete(doc.field, doc.label)}
                      disabled={deleteDocumentMutation.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Supprimer
                    </button>
                  </>
                ) : (
                  <>
                    <label className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors cursor-pointer">
                      <Upload className="h-3.5 w-3.5" />
                      {uploadDecompteMutation.isPending || uploadDemandeTransfertMutation.isPending || uploadInstructionMutation.isPending ? 'Téléversement...' : 'Téléverser'}
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(doc.field.replace('_document', ''), file);
                            e.target.value = '';
                          }
                        }}
                        accept=".pdf"
                        disabled={uploadDecompteMutation.isPending || uploadDemandeTransfertMutation.isPending || uploadInstructionMutation.isPending}
                      />
                    </label>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Multi-fondation Documents: Contrats de regroupement (from customer) */}
      {pensionFund.customer && (
        <>
          <h3 className="text-xl font-semibold text-gray-900 mb-6 mt-10">Contrats de regroupement par fondation</h3>
          {pensionFund.customer.contrat_regroupement_documents && pensionFund.customer.contrat_regroupement_documents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pensionFund.customer.contrat_regroupement_documents.map((doc: MultiFondationDocument) => (
                <div
                  key={doc.id}
                  className="relative border-2 rounded-xl p-5 transition-all duration-200 border-green-200 bg-green-50 hover:shadow-md"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100">
                        <FileText className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">Contrat regroupement</h4>
                        <p className="text-xs mt-1 text-green-600">{doc.fondation_name}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <a
                      href={getFileUrl(doc.file_path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      Voir
                    </a>
                    <a
                      href={getFileUrl(doc.file_path)}
                      download
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Télécharger
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              Aucun contrat de regroupement
            </div>
          )}

          {/* Multi-fondation Documents: Instructions (from customer) */}
          <h3 className="text-xl font-semibold text-gray-900 mb-6 mt-10">Instructions par fondation</h3>
          {pensionFund.customer.instruction_documents && pensionFund.customer.instruction_documents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pensionFund.customer.instruction_documents.map((doc: MultiFondationDocument) => (
                <div
                  key={doc.id}
                  className="relative border-2 rounded-xl p-5 transition-all duration-200 border-green-200 bg-green-50 hover:shadow-md"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100">
                        <FileText className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">Instruction</h4>
                        <p className="text-xs mt-1 text-green-600">{doc.fondation_name}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <a
                      href={getFileUrl(doc.file_path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      Voir
                    </a>
                    <a
                      href={getFileUrl(doc.file_path)}
                      download
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Télécharger
                    </a>
                    <button
                      onClick={() => signInstructionMutation.mutate(doc.fondation_name)}
                      disabled={signInstructionMutation.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <FileSignature className="h-3.5 w-3.5" />
                      {signInstructionMutation.isPending ? 'Signature...' : 'Signer'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              Aucune instruction
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PensionFundDocuments;
